import { useEffect, type CSSProperties } from 'react'
import { Info, X } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import type { ResidentPaymentInstructionsPanelProps } from '../../types'
import { RESIDENT_INSTRUCTIONS_PANEL_WIDTH_PX } from '../../constants/instructionsLayout'
import { RESIDENT_PAYMENT_INSTRUCTION_STEPS } from '../../constants/paymentInstructions'

/**
 * Right-side instructions panel — same experience as Application underwrite
 * checklist (`ChecklistSidebar`): fixed right panel, listing stays usable and
 * shrinks beside it (no modal backdrop / body scroll lock).
 */
export function ResidentPaymentInstructionsPanel({
  isOpen,
  onClose,
}: ResidentPaymentInstructionsPanelProps) {
  useEffect(() => {
    if (!isOpen) return
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <aside
      id="resident-list-instructions-panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="resident-list-instructions-title"
      data-testid="resident-list-instructions-panel"
      className={cn(
        'fixed right-0 top-0 z-[10000] flex h-[100dvh] max-h-[100dvh] w-full flex-col border-l border-slate-200/80 bg-gradient-to-b from-[#f8fafc] to-[#f1f5f9] shadow-[-8px_0_32px_rgba(15,23,42,0.12)]',
        'sm:w-[min(100vw,var(--resident-instructions-width))]',
      )}
      style={
        {
          '--resident-instructions-width': `${RESIDENT_INSTRUCTIONS_PANEL_WIDTH_PX}px`,
        } as CSSProperties
      }
    >
      <div
        data-testid="resident-list-instructions-drawer-header"
        className="sticky top-0 z-[2] flex items-start justify-between gap-3 border-b border-slate-200/90 bg-white px-5 py-4"
      >
        <div className="flex min-w-0 items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-100 to-blue-100 text-[#1597ff]">
            <Info className="h-5 w-5" strokeWidth={2.5} aria-hidden />
          </span>
          <h2
            id="resident-list-instructions-title"
            data-testid="resident-list-instructions-title"
            className="m-0 pt-1.5 text-base font-bold leading-snug text-slate-900 sm:text-lg"
          >
            Instructions: Record Monthly Rental Payments
          </h2>
        </div>
        <button
          type="button"
          data-testid="resident-list-instructions-close-button"
          onClick={onClose}
          className="-mr-1 shrink-0 rounded-full p-2 text-slate-400 transition hover:bg-slate-50 hover:text-slate-600"
          aria-label="Close instructions"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div
        data-testid="resident-list-instructions-body"
        className="flex-1 space-y-6 overflow-y-auto overscroll-contain p-5 sm:p-6"
      >
        <ol className="m-0 flex list-none flex-col gap-5 p-0">
          {RESIDENT_PAYMENT_INSTRUCTION_STEPS.map((step, index) => (
            <li key={index} className="flex gap-3">
              <span
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1597ff] text-sm font-bold text-white"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <p className="m-0 pt-0.5 text-sm leading-relaxed text-slate-700">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* Button kept left-aligned so it never sits under the fixed UserWay
          accessibility launcher (bottom-right of the viewport). */}
      <div
        data-testid="resident-list-instructions-drawer-footer"
        className="flex flex-wrap items-center justify-start gap-4 border-t border-slate-200/90 bg-white px-5 py-4 sm:px-6"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="resident-list-instructions-got-it-button"
          className="min-w-[5.5rem] rounded-lg bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-[rgba(21,151,255,0.22)] transition hover:bg-[#0d7acc]"
        >
          Got it
        </button>
      </div>
    </aside>
  )
}
